<script setup lang="ts">
import { LayoutGap, RangeSlider } from '@dabble/quillui';
import Croppie from 'croppie';
import type { CropData } from 'croppie';
import 'croppie/croppie.css';
import { onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';

const {
  imageUrl,
  initialZoom = 0,
  viewportWidth = 200,
  viewportHeight = 320,
  boundaryWidth = 350,
  boundaryHeight = 450,
  outputWidth,
  outputHeight,
  thumbnailWidth,
  thumbnailHeight,
  viewportShape = 'rectangle',
  fullResultFormat,
  fullResultQuality,
} = defineProps<{
  imageUrl: string;
  initialZoom?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  boundaryWidth?: number;
  boundaryHeight?: number;
  outputWidth?: number;
  outputHeight?: number;
  thumbnailWidth?: number;
  thumbnailHeight?: number;
  viewportShape?: 'rectangle' | 'circle';
  fullResultFormat?: 'png' | 'jpeg';
  fullResultQuality?: number;
}>();

const emit = defineEmits<{
  result: [data: string];
  update: [cropData: CropData];
  ready: [];
}>();

const croppieRef = useTemplateRef<HTMLDivElement>('croppieRef');
let croppieInstance: InstanceType<typeof Croppie> | null = null;
const zoom = defineModel<number>('zoom', { default: undefined });

const minZoom = ref(0);
const maxZoom = ref(1.5);
let ignoreUpdates = false;

if (zoom.value === undefined) {
  zoom.value = initialZoom;
}

const initCroppie = () => {
  if (!croppieRef.value) return;

  const croppieType = viewportShape === 'circle' ? 'circle' : 'square';

  croppieInstance = new Croppie(croppieRef.value, {
    viewport: {
      width: viewportWidth,
      height: viewportHeight,
      type: croppieType,
    },
    boundary: {
      width: boundaryWidth,
      height: boundaryHeight,
    },
    showZoomer: false,
    enableOrientation: true,
  });

  croppieRef.value.addEventListener('update', handleUpdate);

  if (imageUrl) {
    bindImage(imageUrl);
  }
};

const bindImage = async (url: string) => {
  if (!croppieInstance) return;

  ignoreUpdates = true;

  await croppieInstance.bind({ url });
  if (!croppieInstance) return;

  croppieInstance.setZoom(0);
  await nextFrame();
  if (!croppieInstance) return;
  minZoom.value = croppieInstance.get().zoom;

  croppieInstance.setZoom(10);
  await nextFrame();
  if (!croppieInstance) return;
  maxZoom.value = croppieInstance.get().zoom;

  const targetSlider = zoom.value ?? initialZoom;
  applyZoomFromSlider(targetSlider);
  zoom.value = targetSlider;

  ignoreUpdates = false;
  emit('ready');
};

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));

const sliderToCroppieZoom = (slider: number): number => {
  const range = maxZoom.value - minZoom.value;
  return minZoom.value + (slider / 100) * range;
};

const croppieZoomToSlider = (croppieZoom: number): number => {
  const range = maxZoom.value - minZoom.value;
  if (range === 0) return 0;
  const slider = ((croppieZoom - minZoom.value) / range) * 100;
  return Math.max(0, Math.min(100, Math.round(slider)));
};

const applyZoomFromSlider = (sliderValue: number) => {
  if (!croppieInstance) return;
  const croppieZoom = sliderToCroppieZoom(sliderValue);
  croppieInstance.setZoom(croppieZoom);
};

const handleUpdate = (ev: Event) => {
  if (ignoreUpdates) return;

  const cropData = (ev as CustomEvent).detail as CropData;
  emit('update', cropData);

  if (cropData.zoom !== undefined) {
    ignoreUpdates = true;
    zoom.value = croppieZoomToSlider(cropData.zoom);
    ignoreUpdates = false;
  }
};

type ResultSize = 'full' | 'thumbnail' | { width: number; height: number };

const PIXEL_RATIO = 2;

const getResult = async (type: 'base64' | 'blob' | 'html' = 'base64', size: ResultSize = 'full') => {
  if (!croppieInstance) return null;

  let dimensions: { width: number; height: number };
  let format: 'png' | 'jpeg' = 'png';
  let quality = 1;

  if (size === 'full') {
    if (outputWidth == null || outputHeight == null) {
      throw new Error('AvatarCropper: `outputWidth` / `outputHeight` are required when calling getResult("full").');
    }
    dimensions = { width: outputWidth * PIXEL_RATIO, height: outputHeight * PIXEL_RATIO };
    format = fullResultFormat ?? 'png';
    quality = fullResultQuality ?? (format === 'png' ? 1 : 0.9);
  } else if (size === 'thumbnail') {
    if (thumbnailWidth == null || thumbnailHeight == null) {
      throw new Error(
        'AvatarCropper: `thumbnailWidth` / `thumbnailHeight` are required when calling getResult("thumbnail").',
      );
    }
    dimensions = { width: thumbnailWidth * PIXEL_RATIO, height: thumbnailHeight * PIXEL_RATIO };
    format = 'jpeg';
    quality = 0.85;
  } else {
    dimensions = { width: size.width * PIXEL_RATIO, height: size.height * PIXEL_RATIO };
  }

  const result = await croppieInstance.result({
    type,
    size: dimensions,
    format,
    quality,
  });

  if (typeof result === 'string') {
    emit('result', result);
  }

  return result;
};

const getBothResults = async (type: 'base64' | 'blob' = 'base64') => {
  const [full, thumbnail] = await Promise.all([getResult(type, 'full'), getResult(type, 'thumbnail')]);
  return { full, thumbnail };
};

onMounted(() => {
  initCroppie();
});

onUnmounted(() => {
  if (croppieInstance) {
    croppieRef.value?.removeEventListener('update', handleUpdate);
    // Croppie binds its drag listeners on `window` and only removes them in its own
    // mouseup handler, so destroying mid-drag leaves them attached and they throw on
    // the deleted `self.elements`. Fire a synthetic mouseup so Croppie cleans up while
    // its elements still exist. No-op when no drag is in progress.
    window.dispatchEvent(new MouseEvent('mouseup'));
    croppieInstance.destroy();
    croppieInstance = null;
  }
});

watch(
  () => imageUrl,
  (newUrl) => {
    if (newUrl) {
      bindImage(newUrl);
    }
  },
);

watch(zoom, (newZoom) => {
  if (!ignoreUpdates && newZoom !== undefined) {
    applyZoomFromSlider(newZoom);
  }
});

defineExpose({
  getResult,
  getBothResults,
  bindImage,
});
</script>

<template>
  <LayoutGap class="app-avatar-cropper" direction="column" align="center" inline>
    <div ref="croppieRef" class="app-avatar-cropper__container"></div>
    <div class="app-avatar-cropper__controls">
      <RangeSlider v-model="zoom" label="Zoom:" inline :min="0" :max="100" :step="1" />
    </div>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.app-avatar-cropper {
  &__container {
    border-radius: borderRadius('small');
    overflow: hidden;
  }

  &__controls {
    width: 100%;
    max-width: 75%;
    padding: 0 space('xsmall');
  }
}
</style>

<style lang="scss">
.croppie-container {
  .cr-slider-wrap {
    display: none;
  }
}
</style>
